import { CREATIVE_FORMATS, creativeFormatById, creativeFormatForSize } from '@digital-twins/shared-types'

import type { CreativeFormatId } from '@digital-twins/shared-types'
import type { AssistantImageRequest } from '@digital-twins/shared-types/assistant'
import type { TFunction } from 'i18next'

/** "Vertical 9:16": the name is translated, the ratio comes from the formats file. */
export function imageFormatLabel(t: TFunction<'app'>, formatId: CreativeFormatId): string {
  return t('threadImage.approval.formatLabel', {
    name: t(`threadImage.approval.formats.${formatId}.name`),
    ratio: creativeFormatById(formatId)?.ratio ?? '',
  })
}

/** A row's format, or its raw size when a saved quote holds a size off the list. */
export function imageSizeLabel(t: TFunction<'app'>, size: string): string {
  const format = creativeFormatForSize(size)
  return format ? imageFormatLabel(t, format.id) : size.replace('x', ' × ')
}

/** Every selected format, in list order. */
export function imageFormatsLabel(t: TFunction<'app'>, formats: ReadonlyArray<CreativeFormatId>): string {
  return formats.map((id) => imageFormatLabel(t, id)).join(', ')
}

/** The trigger's text: up to two formats in full, then "Square 1:1 +2". */
export function imageFormatsSummary(t: TFunction<'app'>, formats: ReadonlyArray<CreativeFormatId>): string {
  if (formats.length <= 2) {
    return imageFormatsLabel(t, formats)
  }
  return t('threadImage.approval.formatsMore', {
    first: imageFormatLabel(t, formats[0]),
    more: formats.length - 1,
  })
}

/** Under a finished image, only when its request was made in more than one format. */
export function imageFormatCaption(
  t: TFunction<'app'>,
  request: AssistantImageRequest | undefined,
  formatId: string | undefined,
  selection: ReadonlyArray<CreativeFormatId> | null | undefined,
): string | null {
  const format = CREATIVE_FORMATS.find((candidate) => candidate.id === formatId)
  if (!request || !format || (selection?.length ?? 0) < 2) {
    return null
  }
  return t('threadImage.approval.captionFormat', {
    purpose: request.arguments.purpose,
    format: imageFormatLabel(t, format.id),
  })
}
